import { Chip, Stack } from '@mui/material';
import UPButton from 'components/button';
import { UPTanStackTableColumn } from 'components/tanstack-table';
import { TFunction } from 'i18next';
import {
  CONTRACT_COMPLIANCE__PLANNING_COLUMN_API_USER,
  CONTRACT_COMPLIANCE__PLANNING_COLUMN_APIS,
  CONTRACT_COMPLIANCE__PLANNING_COLUMN_PERMISSIONS,
  CONTRACT_COMPLIANCE__PLANNING_COLUMN_ROLE,
  GENERAL__EDIT,
} from 'translations/constants';
import { IPlanningInThePastEntry } from 'types/contract-compliance/IContractCompliance';
import {
  ROLE_LABEL_KEYS,
  ROLE_SLUGS,
  STATE_CHIP_COLORS,
  STATE_SHORT_LABEL_KEYS,
  TEST_ID,
  WORKER_CLASSES,
} from './constants';
import { buildStates } from './utils';

/** "BED: Full" per worker class, coloured by state. A plain MUI Chip, as in the listbox's selected chips. */
const renderPermissionChips = (t: TFunction, entry: IPlanningInThePastEntry, testIdPrefix: string): JSX.Element => {
  const states = buildStates(entry);

  return (
    <Stack direction="row" flexWrap="wrap" gap="6px">
      {WORKER_CLASSES.map(workerClass => (
        <Chip
          key={workerClass}
          size="small"
          label={`${workerClass}: ${t(STATE_SHORT_LABEL_KEYS[states[workerClass]])}`}
          sx={{ ...STATE_CHIP_COLORS[states[workerClass]], fontWeight: 700, borderRadius: '5px' }}
          data-testid={`${testIdPrefix}-chip-${workerClass}`}
        />
      ))}
    </Stack>
  );
};

/** The API(s) an override applies to, as pills. */
const renderApiPills = (entry: IPlanningInThePastEntry, testIdPrefix: string): JSX.Element => (
  <Stack direction="row" flexWrap="wrap" gap="6px">
    {entry.apiScopes.map(scope => (
      <Chip key={scope} size="small" variant="outlined" label={scope} data-testid={`${testIdPrefix}-api-${scope}`} />
    ))}
  </Stack>
);

/** Read-only rows ending with an Edit button; the three roles are a fixed short list. */
export const buildRoleDefaultColumns = (
  t: TFunction,
  onEdit: (entry: IPlanningInThePastEntry) => void,
): UPTanStackTableColumn<IPlanningInThePastEntry>[] => [
  {
    id: 'role',
    header: t(CONTRACT_COMPLIANCE__PLANNING_COLUMN_ROLE),
    cell: ({ row }) => (
      <span data-testid={`${TEST_ID}-role-${ROLE_SLUGS[row.role]}`}>{t(ROLE_LABEL_KEYS[row.role])}</span>
    ),
  },
  {
    id: 'permissions',
    header: t(CONTRACT_COMPLIANCE__PLANNING_COLUMN_PERMISSIONS),
    cell: ({ row }) => renderPermissionChips(t, row, `${TEST_ID}-role-${ROLE_SLUGS[row.role]}`),
  },
  {
    id: 'edit',
    header: '',
    enableSorting: false,
    align: 'right',
    cell: ({ row }) => (
      <UPButton
        text={t(GENERAL__EDIT)}
        outlined
        onClick={() => onEdit(row)}
        testId={`${TEST_ID}-role-edit-${ROLE_SLUGS[row.role]}`}
      />
    ),
  },
];

/** Read-only rows ending with an Edit button (exception list pattern); ids carry the override's id, never an index. */
export const buildOverrideColumns = (
  t: TFunction,
  onEdit: (entry: IPlanningInThePastEntry) => void,
): UPTanStackTableColumn<IPlanningInThePastEntry>[] => [
  {
    id: 'apiUser',
    header: t(CONTRACT_COMPLIANCE__PLANNING_COLUMN_API_USER),
    cell: ({ row }) => <span data-testid={`${TEST_ID}-override-user-${row.id}`}>{row.apiUserName}</span>,
  },
  {
    id: 'apis',
    header: t(CONTRACT_COMPLIANCE__PLANNING_COLUMN_APIS),
    cell: ({ row }) => renderApiPills(row, `${TEST_ID}-override-${row.id}`),
  },
  {
    id: 'permissions',
    header: t(CONTRACT_COMPLIANCE__PLANNING_COLUMN_PERMISSIONS),
    cell: ({ row }) => renderPermissionChips(t, row, `${TEST_ID}-override-${row.id}`),
  },
  {
    id: 'edit',
    header: '',
    enableSorting: false,
    align: 'right',
    cell: ({ row }) => (
      <UPButton
        text={t(GENERAL__EDIT)}
        outlined
        onClick={() => onEdit(row)}
        testId={`${TEST_ID}-override-edit-${row.id}`}
      />
    ),
  },
];
